// Pure data side of MapCanvas (WEB_PLAN.md §5.7, §5.8): screen inputs → GeoJSON, the cluster color expression, and
// the bounds to fit. No MapLibre import, so it runs in unit tests.
//
// Cluster color: iOS colors a cluster bubble by its most common member (MapClustering.swift); MapLibre clusters on the
// GPU-side worker, so we count each member color with `clusterProperties` (one counter per distinct color) and pick
// the largest counter with a `case` expression. Ties go to the color that is most common on the whole map, then the
// lower hex, so a cluster does not flicker between equal colors as you pan.

import type { Feature, FeatureCollection, Point, Polygon, Position } from 'geojson';
import { boundsOf, centroid, isValidCoordinate, type Coordinate } from '@/domain/geo';
import { GT_COLORS, normalizeHex, readableTextOn } from '@/ui/color';

// MARK: - Inputs

/** One store pin. `color` is any "#RRGGBB" (unparseable → gray, like iOS); `label` is drawn on the pin. */
export interface MapPoint {
  id: string;
  coordinate: Coordinate | null | undefined;
  color: string;
  /** Priority letter or visit count (`pinLabel`); empty/undefined draws a plain pin. */
  label?: string | undefined;
}

/** A territory shape: an open ring of ≥ 3 points (the first point is not repeated), as stored on the wire. */
export interface MapPolygon {
  id: string;
  name: string;
  color: string;
  points: readonly Coordinate[];
}

/** A rep's last known position. `color` is the presence color; `label` the initials drawn inside. */
export interface MapMarker {
  id: string;
  coordinate: Coordinate | null | undefined;
  color: string;
  label?: string | undefined;
  /** Hover text ("Jane Doe · Online · 5 minutes ago"). */
  title?: string | undefined;
}

/** `[west, south, east, north]`, MapLibre's LngLatBounds array form. */
export type MapBounds = [number, number, number, number];

// MARK: - GeoJSON

export interface PointProps {
  id: string;
  color: string;
  /** Text color that reads on `color` (white or navy). */
  ink: string;
  label: string;
}

export interface PolygonProps {
  id: string;
  name: string;
  color: string;
}

export interface MarkerProps {
  id: string;
  color: string;
  ink: string;
  label: string;
  title: string;
}

export type PointCollection = FeatureCollection<Point, PointProps>;
export type PolygonCollection = FeatureCollection<Polygon, PolygonProps>;
export type PolygonLabelCollection = FeatureCollection<Point, PolygonProps>;
export type MarkerCollection = FeatureCollection<Point, MarkerProps>;

const position = (c: Coordinate): Position => [c.longitude, c.latitude];

function collection<G extends Point | Polygon, P>(features: Feature<G, P>[]): FeatureCollection<G, P> {
  return { type: 'FeatureCollection', features };
}

// `readableTextOn` runs a contrast calculation; pins share a handful of colors, so remember the answers.
const inkCache = new Map<string, string>();
function inkFor(hex: string): string {
  let ink = inkCache.get(hex);
  if (ink === undefined) {
    ink = readableTextOn(hex);
    inkCache.set(hex, ink);
  }
  return ink;
}

/** Store pins → points. Pins without a valid coordinate (missing, out of range, or the (0,0) placeholder) are left out. */
export function pointCollection(points: readonly MapPoint[]): PointCollection {
  const features: Feature<Point, PointProps>[] = [];
  for (const p of points) {
    if (!isValidCoordinate(p.coordinate)) continue;
    const color = normalizeHex(p.color);
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: position(p.coordinate) },
      properties: { id: p.id, color, ink: inkFor(color), label: p.label ?? '' },
    });
  }
  return collection(features);
}

/**
 * The pins whose id is in `ids` (lower-case keys, `idSet`), for the selection halo under them (map multi-select).
 * Only drawn pins can be highlighted, so a selected store that the filter hides has no halo.
 */
export function highlightCollection(points: PointCollection, ids: ReadonlySet<string> | null | undefined): PointCollection {
  if (!ids || ids.size === 0) return collection([]);
  return collection(points.features.filter((f) => ids.has(f.properties.id.toLowerCase())));
}

/** The valid points of a ring, or null when fewer than 3 remain (iOS draws nothing for those). */
function usableRing(points: readonly Coordinate[]): Coordinate[] | null {
  const ring = points.filter((c) => isValidCoordinate(c));
  return ring.length >= 3 ? ring : null;
}

/** Territory shapes → closed GeoJSON polygons (the wire ring is open; GeoJSON repeats the first point). */
export function polygonCollection(polygons: readonly MapPolygon[]): PolygonCollection {
  const features: Feature<Polygon, PolygonProps>[] = [];
  for (const p of polygons) {
    const ring = usableRing(p.points);
    if (!ring) continue;
    const coords = ring.map(position);
    coords.push(coords[0] as Position);
    features.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [coords] },
      properties: { id: p.id, name: p.name, color: normalizeHex(p.color) },
    });
  }
  return collection(features);
}

/** One label point per drawable territory, at `Geo.centroid` (the vertex average iOS labels with). */
export function polygonLabelCollection(polygons: readonly MapPolygon[]): PolygonLabelCollection {
  const features: Feature<Point, PolygonProps>[] = [];
  for (const p of polygons) {
    const ring = usableRing(p.points);
    const c = ring ? centroid(ring) : undefined;
    if (!c || p.name.trim() === '') continue;
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: position(c) },
      properties: { id: p.id, name: p.name, color: normalizeHex(p.color) },
    });
  }
  return collection(features);
}

/** Rep positions → points (invalid coordinates left out, as iOS `locatedReps`). */
export function markerCollection(markers: readonly MapMarker[]): MarkerCollection {
  const features: Feature<Point, MarkerProps>[] = [];
  for (const m of markers) {
    if (!isValidCoordinate(m.coordinate)) continue;
    const color = normalizeHex(m.color);
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: position(m.coordinate) },
      properties: { id: m.id, color, ink: inkFor(color), label: m.label ?? '', title: m.title ?? '' },
    });
  }
  return collection(features);
}

// MARK: - Clusters

/** MapLibre clustering settings (WEB_PLAN.md §5.7). */
export const CLUSTER_RADIUS = 50;
export const CLUSTER_MAX_ZOOM = 14;
/**
 * At most this many colors get a counter. Each counter costs a reduce step per point in the worker and the pick
 * expression grows with the square of the count; pins rarely use more than the 12-color palette. Members whose color
 * is beyond the cap simply do not vote.
 */
export const MAX_CLUSTER_COLORS = 16;

/** A JSON MapLibre expression (typed loosely here; MapCanvas hands it to the style as-is). */
export type JsonExpression = readonly (string | number | boolean | JsonExpression)[];

/** The counter property for palette entry `i` (prefixed so it never collides with `point_count` and friends). */
export function clusterCountKey(i: number): string {
  return `gt_c${i}`;
}

/**
 * The colors that get a cluster counter: distinct pin colors, most common first (ties by hex), capped at `max`. The
 * order is also the tie-break order of `clusterColorExpression`.
 */
export function clusterPalette(points: PointCollection, max: number = MAX_CLUSTER_COLORS): string[] {
  const counts = new Map<string, number>();
  for (const f of points.features) counts.set(f.properties.color, (counts.get(f.properties.color) ?? 0) + 1);
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .slice(0, Math.max(0, max))
    .map(([hex]) => hex);
}

/** Identity of a palette, to know when the clustered source must be rebuilt (clusterProperties are fixed at creation). */
export function paletteKey(palette: readonly string[]): string {
  return palette.join(',');
}

/**
 * `clusterProperties` for the GeoJSON source: per palette color, a sum of 1 for each member with that color
 * (`[reduce operator, map expression]`).
 */
export function clusterColorProperties(palette: readonly string[]): Record<string, [string, JsonExpression]> {
  const out: Record<string, [string, JsonExpression]> = {};
  palette.forEach((hex, i) => {
    out[clusterCountKey(i)] = ['+', ['case', ['==', ['get', 'color'], hex], 1, 0]];
  });
  return out;
}

/**
 * Picks `values[i]` for the palette color with the largest counter on the cluster (the most common member color);
 * `fallback` when no counted color is present. Branch i wins when its counter is positive and at least every later
 * counter: earlier branches already lost, so some later counter (or nothing) beats them, and ties keep palette order.
 */
function argmaxExpression(palette: readonly string[], values: readonly string[], fallback: string): JsonExpression | string {
  if (palette.length === 0) return fallback;
  const get = (i: number): JsonExpression => ['get', clusterCountKey(i)];
  const branches: (JsonExpression | string)[] = [];
  for (let i = 0; i < palette.length; i++) {
    const conditions: JsonExpression[] = [['>', get(i), 0]];
    for (let j = i + 1; j < palette.length; j++) conditions.push(['>=', get(i), get(j)]);
    branches.push(conditions.length === 1 ? (conditions[0] as JsonExpression) : ['all', ...conditions]);
    branches.push(values[i] as string);
  }
  return ['case', ...branches, fallback];
}

/** The cluster bubble color: its most common member color (gray when none of its colors is counted). */
export function clusterColorExpression(palette: readonly string[], fallback: string = GT_COLORS.grey): JsonExpression | string {
  return argmaxExpression(palette, palette, fallback);
}

/** The count text color matching `clusterColorExpression` (white or navy, whichever reads on that bubble). */
export function clusterInkExpression(palette: readonly string[], fallback: string = GT_COLORS.grey): JsonExpression | string {
  return argmaxExpression(palette, palette.map(inkFor), inkFor(fallback));
}

/**
 * Bubble radius by member count (px). Bigger clusters read as bigger, with steps rather than a smooth curve so
 * neighbouring bubbles of similar size do not look subtly different.
 */
export const CLUSTER_RADIUS_EXPRESSION: JsonExpression = ['step', ['get', 'point_count'], 15, 10, 18, 50, 22, 250, 26, 1000, 30];

// MARK: - Bounds

/** Bounding box of everything drawable (points, then territory rings, then markers); undefined when nothing is. */
export function dataBounds(input: {
  points?: PointCollection;
  polygons?: PolygonCollection;
  markers?: MarkerCollection;
}): MapBounds | undefined {
  const coords: Coordinate[] = [];
  const add = (p: Position) => coords.push({ longitude: p[0] as number, latitude: p[1] as number });
  for (const f of input.points?.features ?? []) add(f.geometry.coordinates);
  for (const f of input.polygons?.features ?? []) for (const p of f.geometry.coordinates[0] ?? []) add(p);
  for (const f of input.markers?.features ?? []) add(f.geometry.coordinates);
  return boundsOf(coords);
}

/** Bounds of a list of coordinates (the `fitBounds(coords)` form of the imperative API). */
export function coordinateBounds(coords: readonly (Coordinate | null | undefined)[]): MapBounds | undefined {
  return boundsOf(coords);
}

/**
 * Padding for `fitBounds` that the container can actually hold: MapLibre refuses to move when the padding is larger
 * than the viewport (a small detail-page mini-map), so cap it at a quarter of the shorter side.
 */
export function fitPadding(requested: number, width: number, height: number): number {
  const cap = Math.floor(Math.min(width, height) / 4);
  return Math.max(0, Math.min(requested, cap));
}
